import { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { clearLoungeToken, getLoungeTokenInfo } from '../utils/loungeToken';

// Thank you image from the existing thank you page
const THANK_YOU_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2023/11/Thank-You-Graphic.png';

// Background image
const BACKGROUND_IMAGE =
  'https://clubdynasty.ca/wp-content/uploads/2021/06/hall-1.jpg';

// Token expiration check interval (1 second)
const CHECK_INTERVAL = 1000;

// iPad-first breakpoints
// iPad Portrait: 768px - 1024px
// iPad Landscape: 1024px - 1366px
const BREAKPOINTS = {
  mobile: 767, // Below iPad
  ipadPortrait: { min: 768, max: 1023 },
  ipadLandscape: { min: 1024, max: 1366 },
  desktop: 1367, // Above iPad
} as const;

const LoungeSurveyThankYouScreen = () => {
  const navigate = useNavigate();

  // iPad-first responsive breakpoints
  const isIpadPortrait = useMediaQuery(
    `(min-width:${BREAKPOINTS.ipadPortrait.min}px) and (max-width:${BREAKPOINTS.ipadPortrait.max}px)`,
  );
  const isIpadLandscape = useMediaQuery(
    `(min-width:${BREAKPOINTS.ipadLandscape.min}px) and (max-width:${BREAKPOINTS.ipadLandscape.max}px)`,
  );
  const isIpad = isIpadPortrait || isIpadLandscape;
  const isDesktop = useMediaQuery(`(min-width:${BREAKPOINTS.desktop}px)`);

  // Track remaining time for display
  const [remainingTime, setRemainingTime] = useState<number>(0);

  // Check token expiration and update remaining time
  useEffect(() => {
    const checkTokenExpiration = () => {
      const tokenInfo = getLoungeTokenInfo();

      if (!tokenInfo) {
        // Token expired or doesn't exist, redirect to lounge access
        clearLoungeToken();
        navigate('/lounge-access', { replace: true });
        return;
      }

      setRemainingTime(tokenInfo.remainingMs);
    };

    // Initial check
    checkTokenExpiration();

    // Set up interval for checking
    const interval = setInterval(checkTokenExpiration, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [navigate]);

  // Format remaining time as MM:SS
  const formattedTime = useMemo(() => {
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [remainingTime]);

  // Handle back to home/lounge access
  const handleBackToHome = useCallback(() => {
    clearLoungeToken();
    navigate('/lounge-access');
  }, [navigate]);

  // iPad-first responsive sizing
  // Primary: iPad gets the best experience
  // Secondary: Mobile and desktop adapt from iPad base
  const imageWidth = useMemo(() => {
    if (isIpadLandscape) return 520;
    if (isIpadPortrait) return 480;
    if (isDesktop) return 450;
    return 280; // mobile
  }, [isIpadPortrait, isIpadLandscape, isDesktop]);

  // Typography sizing optimized for iPad readability
  const getTitleFontSize = () => {
    if (isIpadLandscape) return '4.5rem';
    if (isIpadPortrait) return '4rem';
    if (isDesktop) return '3.5rem';
    return '2.25rem'; // mobile
  };

  const getBodyFontSize = () => {
    if (isIpadLandscape) return '1.5rem';
    if (isIpadPortrait) return '1.375rem';
    if (isDesktop) return '1.25rem';
    return '1rem'; // mobile
  };

  // Touch-friendly button sizing (min 44px for iOS guidelines)
  const getButtonStyles = () => {
    if (isIpadLandscape) {
      return {
        minWidth: 280,
        minHeight: 64,
        fontSize: '1.35rem',
        px: 6,
        py: 2,
      };
    }
    if (isIpadPortrait) {
      return {
        minWidth: 260,
        minHeight: 60,
        fontSize: '1.25rem',
        px: 5,
        py: 1.75,
      };
    }
    if (isDesktop) {
      return {
        minWidth: 220,
        minHeight: 56,
        fontSize: '1.15rem',
        px: 4,
        py: 1.5,
      };
    }
    // Mobile - still touch friendly
    return {
      minWidth: 160,
      minHeight: 52,
      fontSize: '1.05rem',
      px: 3,
      py: 1.25,
    };
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Background image
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(${BACKGROUND_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        // iPad-first padding
        py: isIpad ? 6 : isDesktop ? 5 : 4,
        px: isIpad ? 4 : 2,
        // Prevent content shift on orientation change
        overflow: 'hidden',
      }}
    >
      <Container
        maxWidth={isIpadLandscape ? 'lg' : isIpadPortrait ? 'md' : 'md'}
        sx={{
          // Semi-transparent card for content
          backgroundColor: 'rgba(255, 255, 255, 0.75)',
          borderRadius: isIpad ? 4 : 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          // iPad-first horizontal padding
          px: isIpad ? 6 : isDesktop ? 5 : 3,
          py: isIpad ? 6 : isDesktop ? 5 : 4,
          // Ensure content is centered and properly spaced on iPad
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Timer indicator - optimized for iPad visibility */}
        {remainingTime > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: isIpad ? 4 : 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                px: isIpad ? 3 : 2,
                py: isIpad ? 1.5 : 1,
                borderRadius: 2,
                backgroundColor:
                  remainingTime < 60000 ? 'error.light' : 'grey.100',
                color:
                  remainingTime < 60000
                    ? 'error.contrastText'
                    : 'text.secondary',
                // Subtle shadow for better visibility on iPad
                boxShadow: isIpad ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: isIpad ? '1.2rem' : isDesktop ? '1rem' : '0.9rem',
                  fontWeight: 600,
                  letterSpacing: isIpad ? '0.02em' : 0,
                }}
              >
                Session expires in: {formattedTime}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Thank you image - iPad optimized sizing */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            mb: isIpad ? 4 : 2,
            // Prevent layout shift during image load
            minHeight: isIpad ? imageWidth * 0.6 : 'auto',
          }}
        >
          <img
            src={THANK_YOU_IMAGE}
            alt="Thank you"
            width={imageWidth}
            style={{
              maxWidth: '100%',
              height: 'auto',
              // Smooth scaling for orientation changes
              transition: 'width 0.2s ease-in-out',
            }}
          />
        </Box>

        {/* Thank you title - iPad optimized typography */}
        <Typography
          variant="h1"
          align="center"
          sx={{
            mb: isIpad ? 4 : 2.5,
            fontWeight: 700,
            fontSize: getTitleFontSize(),
            lineHeight: 1.2,
            color: 'text.primary',
            // Prevent text reflow on orientation change
            whiteSpace: 'nowrap',
          }}
        >
          Thank You
        </Typography>

        {/* Thank you message - iPad optimized for comfortable reading */}
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{
            mb: isIpad ? 6 : 4,
            px: isIpad ? 3 : 1,
            fontSize: getBodyFontSize(),
            lineHeight: isIpad ? 1.9 : 1.75,
            // Optimal line length for readability on iPad
            maxWidth: isIpadLandscape ? 800 : isIpadPortrait ? 680 : '100%',
            mx: 'auto',
            // Improve text rendering on iPad
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          We sincerely appreciate you taking the time to submit the survey and
          share your valuable feedback with us. It is through the insights and
          opinions of our customers that we are able to continuously improve our
          services and exceed expectations. Your feedback will help us identify
          areas where we can improve and ensure that we maintain the highest
          level of customer satisfaction. Thank you again for your support and
          for choosing our services.
        </Typography>

        {/* Back to home button - Touch-friendly sizing for iPad */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: isIpad ? 2 : 1,
          }}
        >
          <Button
            variant="contained"
            disableElevation
            onClick={handleBackToHome}
            sx={{
              ...getButtonStyles(),
              borderRadius: isIpad ? 2 : 1,
              borderWidth: isIpad ? 2 : 1,
              '&:hover': {
                borderWidth: isIpad ? 2 : 1,
              },
              // Ensure touch target is comfortable on iPad
              touchAction: 'manipulation',
              // Prevent double-tap zoom on iOS
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default LoungeSurveyThankYouScreen;
